import React, { FC, useState } from 'react';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  LightMode,
  ListItem,
  UnorderedList,
  useColorModeValue as mode
} from '@chakra-ui/react';
import { ForgotPassword } from '../../libs/authentication';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { UnderlineLink } from '../chakraUIPro/UnderlineLink';

type ForgotPasswordFormProps = {
  // TODO:
  // eslint-disable-next-line no-unused-vars
  callback(err: string | null, data?: { email: string }): void;
};

const ForgotPasswordForm: FC<ForgotPasswordFormProps> = ({ callback }) => {
  const { logEvent } = useAnalytics();

  const [error, setError] = useState('');

  async function onForgotPassword(evt: any) {
    const { email } = evt;
    setError('');

    logEvent({
      action: 'Forgot password',
      category: 'Sign in'
    });

    // try to send verification code
    const codeSent = await ForgotPassword({ username: email }).catch((err: any) => {
      if (err.message === 'Email not yet confirmed') {
        return callback(err.message, { email });
      }

      console.error({ action: 'onForgotPassword:ForgotPassword:error', error: err });
      setError(err.message);
      return false;
    });

    if (!codeSent) {
      return;
    }

    callback(null, { email });
  }

  async function onCancel() {
    setError('');
    callback(null);
  }

  const forgotPasswordSchema = yup.object({
    email: yup.string().email('Must be a valid email').required('Email is required')
  });

  return (
    // eslint-disable-next-line react/jsx-no-bind
    <Formik validationSchema={forgotPasswordSchema} onSubmit={onForgotPassword} initialValues={{ email: '' }}>
      {({ isSubmitting, handleChange, handleBlur, values, touched, errors }) => (
        <Form>
          {/* Errors */}
          {error.length > 0 && (
            <Alert status='error' mb='4' borderRadius='md'>
              <AlertIcon />
              {error}
            </Alert>
          )}

          {/* Email */}
          <FormControl
            // TODO:
            // @ts-ignore
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            isInvalid={touched.email && !!errors.email}
          >
            <FormLabel srOnly>Email</FormLabel>
            <Input
              id='email'
              size='lg'
              name='email'
              type='email'
              autoComplete='email'
              required
              placeholder='Email address'
              bg={mode('white', 'gray.700')}
              fontSize='md'
              focusBorderColor='blue.300'
            />

            {/* @ts-ignore TODO: Fix */}
            <FormErrorMessage>{errors.email}</FormErrorMessage>

            <FormHelperText mt={2}>
              How to reset your password:
              <UnorderedList px='4' pt='2'>
                <ListItem>Enter your registered email address.</ListItem>
                <ListItem>Click Reset Password.</ListItem>
                <ListItem>Check your inbox for an email with a verification code.</ListItem>
                <ListItem>Enter the verification code and your new password.</ListItem>
              </UnorderedList>
            </FormHelperText>
          </FormControl>

          <Flex align='center' justify='space-between' mt='8'>
            <Box />
            {/* eslint-disable-next-line react/jsx-no-bind */}
            <UnderlineLink fontSize='sm' onClick={onCancel}>
              Cancel
            </UnderlineLink>
          </Flex>

          <LightMode>
            <Button
              size='lg'
              type='submit'
              mt='8'
              w='full'
              colorScheme='blue'
              fontSize='md'
              fontWeight='bold'
              isLoading={isSubmitting}
              isDisabled={!!errors.email || !values.email}
            >
              Reset password
            </Button>
          </LightMode>
        </Form>
      )}
    </Formik>
  );
};

export default ForgotPasswordForm;
